"use client";

import type { PanchangResponse } from "@/lib/api/panchang";
import {
  buildTithiPurposeFacts,
  TITHI_COPY,
  TITHI_PURPOSES,
  type TithiLanguage,
  type TithiPurposeId,
} from "../tithi-today.copy";

export default function TithiIntentChooser({
  language,
  cityName,
  result,
  selectedPurpose,
  onPurposeChange,
}: {
  language: TithiLanguage;
  cityName: string;
  result?: PanchangResponse;
  selectedPurpose: TithiPurposeId | null;
  onPurposeChange: (purpose: TithiPurposeId) => void;
}) {
  const copy = TITHI_COPY[language];
  const purposes = TITHI_PURPOSES[language];
  const selected = purposes.find((purpose) => purpose.id === selectedPurpose);

  return (
    <section className="card-sacred mb-5 min-h-[360px] p-5" aria-labelledby="tithi-purpose-title">
      <h3 id="tithi-purpose-title" className="text-subtitle-small font-bold text-ish-dark">
        {copy.purposeTitle}
      </h3>
      <p className="mt-1 text-body-small text-ish-text-muted">{copy.purposeHint}</p>
      <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup">
        {purposes.map((purpose) => {
          const active = purpose.id === selectedPurpose;
          return (
            <button
              key={purpose.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onPurposeChange(purpose.id)}
              className={`min-h-12 rounded-lg border px-3 py-2 text-body-small font-semibold transition ${
                active
                  ? "border-ish-accent bg-ish-accent-light text-intent-brand"
                  : "border-ish-border bg-surface-raised text-ish-dark hover:border-ish-accent-border"
              }`}
            >
              {purpose.label}
            </button>
          );
        })}
      </div>
      <div className="mt-5 border-t border-ish-border pt-4" aria-live="polite">
        {!selected && <p className="text-body-small text-ish-text-faint">{copy.choosePurpose}</p>}
        {selected && !result && (
          <p className="text-body-small text-ish-text-muted">{copy.previewUpdating}</p>
        )}
        {selected && result && (
          <TithiPurposePreview
            language={language}
            cityName={cityName}
            purpose={selected.id}
            purposeDescription={selected.description}
            result={result}
          />
        )}
      </div>
    </section>
  );
}

function TithiPurposePreview({
  language,
  cityName,
  purpose,
  purposeDescription,
  result,
}: {
  language: TithiLanguage;
  cityName: string;
  purpose: TithiPurposeId;
  purposeDescription: string;
  result: PanchangResponse;
}) {
  const copy = TITHI_COPY[language];
  const facts = buildTithiPurposeFacts(purpose, result, language);

  return (
    <div>
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        {copy.previewEyebrow}
      </p>
      <h4 className="mt-1 text-subtitle-small font-bold text-ish-dark">
        {copy.previewTitle} · {cityName}
      </h4>
      <p className="mt-1 text-body-small text-ish-text-muted">{purposeDescription}</p>
      <p className="mt-3 text-body-small leading-relaxed text-ish-text-muted">
        {copy.previewIntro}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-2">
        {facts.map((fact) => (
          <div key={fact.label} className="rounded-lg bg-surface-warm p-2.5">
            <dt className="text-eyebrow uppercase tracking-eyebrow text-ish-text-faint">
              {fact.label}
            </dt>
            <dd className="mt-0.5 text-caption font-semibold text-ish-dark">{fact.value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-caption leading-relaxed text-ish-text-faint">
        {copy.previewBoundary}
      </p>
    </div>
  );
}
